import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import { Badge, Button, Dialog, Field, useToast } from '@akillidernek/ui'
import { getAccessToken } from '../auth-store'
import { getTenantSlug } from '../tenant-store'
import { useTenantSlug } from '../use-tenant'

type MembershipType = components['schemas']['MembershipTypeRead']
type Rapor = components['schemas']['ImportReportRead']

/**
 * Üye listesi içe/dışa aktarım — YES-105.
 *
 * Dosya biçimi CSV ve ekran bunu SÖYLÜYOR: "Excel dosyası" deyip CSV vermek,
 * kullanıcının .xlsx beklediği yerde bir sürprize dönüşür.
 */
export function UyeAktarim({ tipler }: { tipler: MembershipType[] }) {
  const [iceAcik, setIceAcik] = useState(false)

  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setIceAcik(true)}>
        İçe aktar
      </Button>
      <DisaAktar />
      {iceAcik && <IceAktar tipler={tipler} onKapat={() => setIceAcik(false)} />}
    </>
  )
}

function DisaAktar() {
  const toast = useToast()
  const [iniyor, setIniyor] = useState(false)

  async function indir() {
    setIniyor(true)
    try {
      // `api` istemcisi JSON çözüyor; burada ham gövde gerekiyor. Başlıkları
      // elle kuruyoruz çünkü indirme, uygulamanın tek ikili yanıtı.
      const yanit = await fetch('/api/v1/members/export', {
        headers: {
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
          ...(getTenantSlug() ? { 'X-Tenant-Slug': getTenantSlug()! } : {}),
        },
      })
      if (!yanit.ok) {
        toast({
          title: 'İndirilemedi',
          description: yanit.status === 403 ? 'Dışa aktarma yetkiniz yok.' : undefined,
          tone: 'error',
        })
        return
      }
      const blob = await yanit.blob()
      const adres = URL.createObjectURL(blob)
      const baglanti = document.createElement('a')
      baglanti.href = adres
      baglanti.download = 'uyeler.csv'
      baglanti.click()
      URL.revokeObjectURL(adres)
    } finally {
      setIniyor(false)
    }
  }

  return (
    <Button variant="secondary" size="sm" disabled={iniyor} onClick={() => void indir()}>
      {iniyor ? 'Hazırlanıyor…' : 'CSV indir'}
    </Button>
  )
}

function IceAktar({ tipler, onKapat }: { tipler: MembershipType[]; onKapat: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const slug = useTenantSlug()
  const [rapor, setRapor] = useState<Rapor | null>(null)
  const [hata, setHata] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const yukle = useMutation({
    mutationFn: async ({ form, dryRun }: { form: FormData; dryRun: boolean }) => {
      const govde = new FormData()
      govde.append('file', form.get('file') as File)
      govde.append('default_membership_type_id', String(form.get('default_membership_type_id')))
      govde.append('dry_run', String(dryRun))

      const yanit = await fetch('/api/v1/members/import', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
          ...(getTenantSlug() ? { 'X-Tenant-Slug': getTenantSlug()! } : {}),
        },
        body: govde,
      })
      const veri = await yanit.json()
      if (!yanit.ok) throw new Error(veri.detail ?? 'Dosya işlenemedi.')
      return veri as Rapor
    },
    onSuccess: (sonuc) => {
      setRapor(sonuc)
      setHata(null)
      if (!sonuc.dry_run && sonuc.created > 0) {
        // Liste tazelenmezse kullanıcı "aktardım ama görünmüyor" der.
        void queryClient.invalidateQueries({ queryKey: ['members', slug] })
        toast({ title: `${sonuc.created} üye içe aktarıldı`, tone: 'success' })
      }
    },
    onError: (error) => setHata(error instanceof ApiError ? error.problem.detail : error.message),
  })

  const onizlemeTemiz = rapor?.dry_run === true && rapor.errors.length === 0

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title="Üye listesi içe aktar"
      description="Excel'in açtığı CSV. Önce ön izleme yapılır; hiçbir satır yazılmaz."
    >
      <form
        ref={formRef}
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setRapor(null)
          setHata(null)
          yukle.mutate({ form: new FormData(event.currentTarget), dryRun: true })
        }}
      >
        <Field label="Dosya" required hint="En az “Ad” ve “Soyad” sütunları olmalı.">
          {(props) => (
            <input type="file" name="file" accept=".csv,text/csv" className="text-sm" {...props} />
          )}
        </Field>

        <Field
          label="Varsayılan üyelik tipi"
          required
          hint="Dosyada “Üyelik Tipi” sütunu yoksa bu kullanılır."
        >
          {(props) => (
            <select
              name="default_membership_type_id"
              className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
              {...props}
            >
              {tipler.map((tip) => (
                <option key={tip.id} value={tip.id}>
                  {tip.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        {/* İçe aktarılan üyeler BAŞVURU olarak açılıyor; bunu söylemek,
            "aktarttım ama hepsi başvuruda kaldı" sorusunu önlüyor. */}
        <p className="text-xs text-[var(--ad-text-muted)]">
          Aktarılan kayıtlar <strong>başvuru</strong> olarak açılır. Üyeliği başlatmak yönetim
          kurulu kararıdır.
        </p>

        {hata && (
          <p role="alert" className="text-xs text-[var(--ad-danger)]">
            {hata}
          </p>
        )}

        {rapor && <RaporGorunumu rapor={rapor} />}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onKapat}>
            Kapat
          </Button>
          <Button type="submit" variant="secondary" disabled={yukle.isPending}>
            {yukle.isPending ? 'Kontrol ediliyor…' : 'Ön izleme'}
          </Button>
          {/* "Aktar" düğmesi ancak ön izleme TEMİZ geçtikten sonra çıkıyor:
              hatalı dosyayı yazmaya çalışmak, kullanıcıyı temizlik yapmak
              zorunda bırakırdı (sunucu da zaten hiçbir şey yazmıyor). */}
          {onizlemeTemiz && (
            <Button
              type="button"
              disabled={yukle.isPending}
              onClick={() => {
                if (!formRef.current) return
                yukle.mutate({ form: new FormData(formRef.current), dryRun: false })
              }}
            >
              {rapor.created} kaydı aktar
            </Button>
          )}
        </div>
      </form>
    </Dialog>
  )
}

function RaporGorunumu({ rapor }: { rapor: Rapor }) {
  if (rapor.errors.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] px-3 py-2 text-sm">
        <Badge tone="success">Hata yok</Badge>
        <span>
          {rapor.total_rows} satır okundu,{' '}
          {rapor.dry_run
            ? `${rapor.created} kayıt aktarılabilir`
            : `${rapor.created} kayıt aktarıldı`}
          .
        </span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm">
        <Badge tone="warning">{rapor.errors.length} hata</Badge>
        {/* Kaç satır yazıldığını söylemek şart: "yarısı girdi mi?" sorusu
            cevapsız kalmamalı. */}
        <span>Hiçbir satır yazılmadı. Dosyayı düzeltip tekrar yükleyin.</span>
      </div>
      <div className="max-h-56 overflow-y-auto rounded-[var(--radius-control)] border border-[var(--ad-border)]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[var(--ad-surface)]">
            <tr className="text-left text-xs text-[var(--ad-text-muted)]">
              <th className="px-2 py-1 font-medium">Satır</th>
              <th className="px-2 py-1 font-medium">Sütun</th>
              <th className="px-2 py-1 font-medium">Sorun</th>
            </tr>
          </thead>
          <tbody>
            {rapor.errors.map((hata, sira) => (
              <tr key={sira} className="border-t border-[var(--ad-border)]">
                <td className="px-2 py-1 tabular-nums">{hata.line}</td>
                <td className="px-2 py-1">{hata.column}</td>
                <td className="px-2 py-1">{hata.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
